import React from 'react';
import { Alert, Button, Space, Tag, Tooltip } from 'antd';
import { RollPlanStatus } from '../../../services/pairTradingApi';
import {
  bannerKind,
  countdownLabel,
  isDismissible,
  missedRollMessage,
  orphanHeadline,
  planSummary,
  shouldShowBanner,
  warningTone,
} from '../../../utils/rollPlanView';

/**
 * The expiry notice for the pair's futures leg — and, when it is already too
 * late, the way out.
 *
 * Three states, deliberately one component: they are the same fact at different
 * stages, and an operator should never see two of them stacked.
 *
 *  - **expiry** (2 trading days out): "this rolls on Wednesday at 14:00". Can be
 *    dismissed; the dismissal is keyed to the plan, so it comes back if the plan
 *    or the contract changes but not on every reload. That is the whole
 *    anti-spam rule — see `rollWarning` on the BE.
 *  - **missed**: the roll date passed while the bot was stopped. Still fixable
 *    (the contract trades until expiry), so it says how, and cannot be dismissed.
 *  - **orphan**: expiry passed, the exchange settled the futures leg, the stock
 *    leg is still held. Nothing to dismiss and nothing to roll — the pair has to
 *    be rebuilt from the broker's positions.
 */
export interface FuturesRollBannerProps {
  status: RollPlanStatus | null;
  onOpenPlan: () => void;
  onOpenPairImport: () => void;
  onAcknowledge: () => void;
  busy?: boolean;
  compact?: boolean;
}

const FuturesRollBanner: React.FC<FuturesRollBannerProps> = ({
  status,
  onOpenPlan,
  onOpenPairImport,
  onAcknowledge,
  busy,
  compact,
}) => {
  if (!status || !shouldShowBanner(status)) return null;
  const kind = bannerKind(status);

  if (kind === 'orphan') {
    const { orphan } = status;
    return (
      <Alert
        type="error"
        showIcon
        style={{ fontSize: 11, marginBottom: 8 }}
        message={orphanHeadline(orphan)}
        description={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{orphan.reason}</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {orphan.openPairs > 0 && <Tag color="red">{orphan.openPairs} pair(s) in the bot book</Tag>}
              {orphan.derivHeldQty !== '0' && <Tag color="red">{orphan.derivHeldQty} contract(s)</Tag>}
              {orphan.stockLegsHeld.map((leg) => (
                <Tag key={leg.symbol} color="orange">{`${leg.symbol} ${leg.heldQty}`}</Tag>
              ))}
            </div>
            <Space>
              <Button size="small" danger type="primary" onClick={onOpenPairImport}>
                Import pair from broker
              </Button>
              <Tooltip title="Nothing is deleted: the old legs are archived with their history and the new pair is created from the positions your broker reports.">
                <span style={{ cursor: 'help', color: '#848E9C' }}>What does this do?</span>
              </Tooltip>
            </Space>
          </div>
        }
      />
    );
  }

  if (kind === 'missed') {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ fontSize: 11, marginBottom: 8 }}
        message={`Roll overdue — ${countdownLabel(status)}`}
        description={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{missedRollMessage(status)}</span>
            <Space>
              <Button size="small" onClick={onOpenPlan}>Roll settings</Button>
            </Space>
          </div>
        }
      />
    );
  }

  return (
    <Alert
      type={warningTone(status.warning.severity)}
      showIcon
      style={{ fontSize: 11, marginBottom: 8 }}
      message={`${status.plan.contract} — ${countdownLabel(status)}`}
      description={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span>{status.warning.message ?? planSummary(status)}</span>
          {!compact && status.plan.note && (
            <span style={{ color: '#848E9C' }}>{status.plan.note}</span>
          )}
          <Space>
            <Button size="small" onClick={onOpenPlan}>Roll settings</Button>
            {isDismissible(status) && (
              <Tooltip title="Hides this until the plan or the contract changes — you will be told again if either does.">
                <Button size="small" type="text" loading={busy} onClick={onAcknowledge}>
                  Got it
                </Button>
              </Tooltip>
            )}
          </Space>
        </div>
      }
    />
  );
};

export default FuturesRollBanner;
